import FootballStatsCategory from "./football-stats-category";
import { getSafeOrDefault } from "../../../services/common/object-util";
import FootballState from "../state/football-state";

export default class FootballStatsCollection {
    constructor(statLogs = []) {
        this.statLogs = statLogs;
    }

    toJSON() {
        return {
            stats: {
                offense: this.offensiveStatsJSON(),
                defense: this.defensiveStatsJSON(),
                specialTeams: this.specialTeamsStatsJSON(),
                penalty: this.penaltySummary(),
                kicks: this.kicksSummary(),
                rushing: this.rushingStats(),
                misc: this.miscStatsJSON()
            }
        };
    }

    kicksSummary() {
        return {
            kickoff: this.kickOffSummary(),
            kickoffReturn: this.kickOffReturnSummary(),
            fieldGoal: this.fieldGoalSummary(),
            fieldGoalRedzone: this.fieldGoalRedzoneSummary(),
            fieldGoalBlocked: this.fieldGoalBlockedSummary(),
            patKick: this.patKickSummary(),
            deuce: this.deuceSummary(),
            barBall: this.barBallSummary(),
            barBallReturn: this.barBallReturnSummary(),
            reboundNet: this.reboundNetSummary(),
            reboundNetReturn: this.reboundNetReturnSummary()
        };
    }

    rushingStats() {
        return {
            touchdowns: this.statCategorySummarizer(
                FootballStatsCategory.RUSHING_TOUCHDOWN
            )
        };
    }

    offensiveTurnovers() {
        return {
            onDown: this.statCategorySummarizer(
                FootballStatsCategory.TURNOVER_ON_DOWN
            ),
            onDownRedzone: this.statCategorySummarizer(
                FootballStatsCategory.TURNOVER_ON_DOWN_REDZONE
            )
        };
    }

    offensiveStatsJSON() {
        return {
            pass: this.passSummary(),
            passTarget: this.passTargetSummary(),
            rush: this.rushSummary(),
            other: this.otherStatsSummary(),
            fumble: this.offenseFumble(),
            turnovers: this.offensiveTurnovers(),
            total: this.totalOffenseSummary(),
            pat: this.patStatsJSON(),
            downs: this.downStatsJSON(),
            sack: this.offensiveSacks(),
            kneelDown: this.kneelDownSummary(),
            touchdowns: this.touchdownSummary()
        };
    }

    defensiveStatsJSON() {
        return {
            tackle: this.tackleSummary(),
            tackleForLoss: this.tackleForLossSummary(),
            fumble: this.defenseFumble(),
            sack: this.defensiveSacks(),
            turnovers: this.defensiveTurnoverSummary(),
            interceptions: this.defensiveInterceptionSummary(),
            interceptionReturns: this.defensiveInterceptionReturnsSummary(),
            returns: this.returnsSummary(),
            safety: this.safetySummary(),
            blocked: this.fieldGoalBlockedSummary(),
            passBrokenUp: this.passBrokenUpSummary()
        };
    }

    miscStatsJSON() {
        return {
            continue: this.continueSummary(),
            lateral: this.lateralSummary(),
            teamLoss: this.teamLossSummary(),
            updateField: this.updateFieldSummary(),
            fgReturn: this.fgReturnSummary()
        };
    }

    specialTeamsStatsJSON() {
        return {
            punt: this.puntSummary(),
            puntReturn: this.puntReturnSummary()
        };
    }

    patStatsJSON() {
        const patRunStats = this.statLogs.filter(
            (s) => FootballStatsCategory.PAT_RUN === s.category
        );

        const patPassStats = this.statLogs.filter(
            (s) => FootballStatsCategory.PAT_PASS === s.category
        );

        const patComplete = this.statLogs.filter(
            (s) => FootballStatsCategory.PAT_COMPLETE === s.category
        );

        return {
            //TODO: Improve unnecessary iterations.
            rush: {
                1: this.statSummarizer(
                    patRunStats.filter((s) => s.values.code === 1)
                ),
                2: this.statSummarizer(
                    patRunStats.filter((s) => s.values.code === 2)
                ),
                3: this.statSummarizer(
                    patRunStats.filter((s) => s.values.code === 3)
                ),
                4: this.statSummarizer(
                    patRunStats.filter((s) => s.values.code === 4)
                )
            },
            pass: {
                1: this.statSummarizer(
                    patPassStats.filter((s) => s.values.code === 1)
                ),
                2: this.statSummarizer(
                    patPassStats.filter((s) => s.values.code === 2)
                ),
                3: this.statSummarizer(
                    patPassStats.filter((s) => s.values.code === 3)
                ),
                4: this.statSummarizer(
                    patPassStats.filter((s) => s.values.code === 4)
                )
            },
            total: {
                ...this.statSummarizer(patComplete)
            }
        };
    }

    downStatsJSON() {
        const downStats = this.statLogs.filter(
            (s) => FootballStatsCategory.DOWN === s.category
        );

        return {
            //TODO: Improve unnecessary iterations.
            rush: {
                1: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 1 &&
                            s.values.playType === FootballState.Rush
                    )
                ),
                2: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 2 &&
                            s.values.playType === FootballState.Rush
                    )
                ),
                3: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 3 &&
                            s.values.playType === FootballState.Rush
                    )
                ),
                4: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 4 &&
                            s.values.playType === FootballState.Rush
                    )
                )
            },
            pass: {
                1: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 1 &&
                            s.values.playType === FootballState.Pass
                    )
                ),
                2: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 2 &&
                            s.values.playType === FootballState.Pass
                    )
                ),
                3: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 3 &&
                            s.values.playType === FootballState.Pass
                    )
                ),
                4: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 4 &&
                            s.values.playType === FootballState.Pass
                    )
                )
            },
            penalty: {
                1: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 1 &&
                            s.values.playType === FootballState.Penalty
                    )
                ),
                2: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 2 &&
                            s.values.playType === FootballState.Penalty
                    )
                ),
                3: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 3 &&
                            s.values.playType === FootballState.Penalty
                    )
                ),
                4: this.statSummarizer(
                    downStats.filter(
                        (s) =>
                            s.values.down === 4 &&
                            s.values.playType === FootballState.Penalty
                    )
                )
            }
        };
    }

    patKickSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.PAT_KICK);
    }

    fieldGoalRedzoneSummary() {
        return this.statCategorySummarizer(
            FootballStatsCategory.FIELD_GOAL_REDZONE
        );
    }

    fieldGoalSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.FIELD_GOAL);
    }

    fieldGoalBlockedSummary() {
        return this.statCategorySummarizer(
            FootballStatsCategory.FIELD_GOAL_BLOCKED
        );
    }

    deuceSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.DEUCE);
    }

    barBallSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.BAR_BALL);
    }

    barBallReturnSummary() {
        return this.statCategorySummarizer(
            FootballStatsCategory.BAR_BALL_RETURN
        );
    }

    reboundNetSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.REBOUND_NET);
    }

    reboundNetReturnSummary() {
        return this.statCategorySummarizer(
            FootballStatsCategory.REBOUND_NET_RETURN
        );
    }

    kickOffSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.KICK_OFF);
    }

    kickOffReturnSummary() {
        // Bar Ball and Rebound Net returns happen on kickoffs, so their return
        // yards are folded into the kickoff-return totals surfaced in the
        // boxscore and individual stats.
        return this.statCategorySummarizer([
            FootballStatsCategory.KICK_OFF_RETURN,
            FootballStatsCategory.BAR_BALL_RETURN,
            FootballStatsCategory.REBOUND_NET_RETURN
        ]);
    }

    otherStatsSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.CONTINUED);
    }

    passSummary() {
        return this.statCategorySummarizer([
            FootballStatsCategory.PASS,
            FootballStatsCategory.SPIKE
        ]);
    }

    passTargetSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.PASS_TARGET);
    }

    passBrokenUpSummary() {
        return this.statCategorySummarizer(
            FootballStatsCategory.PASS_BROKEN_UP
        );
    }

    kneelDownSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.KNEEL_DOWN);
    }

    rushSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.RUSH);
    }

    penaltySummary() {
        return this.statCategorySummarizer(FootballStatsCategory.PENALTY);
    }

    lateralSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.LATERAL);
    }

    continueSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.CONTINUED);
    }

    teamLossSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.TEAM_LOSS);
    }

    updateFieldSummary() {
        return this.statCategorySummarizer(
            FootballStatsCategory.UPDATE_CONTEXT
        );
    }

    fgReturnSummary() {
        return this.statCategorySummarizer(
            FootballStatsCategory.FIELD_GOAL_RETURN
        );
    }

    touchdownSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.TOUCHDOWN);
    }

    offensiveOtherSummary() {
        return {};
    }

    offensiveTurnoversSummary() {
        return {};
    }

    totalOffenseSummary() {
        return this.statCategorySummarizer([
            FootballStatsCategory.RUSH,
            FootballStatsCategory.PASS,
            FootballStatsCategory.SPIKE
        ]);
    }

    tackleSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.TACKLE);
    }

    tackleForLossSummary() {
        return this.statSummarizer(
            this.statLogs.filter(
                (s) =>
                    FootballStatsCategory.TACKLE === s.category &&
                    s.values?.yards < 0
            )
        );
    }

    safetySummary() {
        return this.statCategorySummarizer(FootballStatsCategory.SAFETY);
    }

    offenseFumble() {
        return {
            continued: this.statSummarizer(
                this.statLogs.filter((s) => {
                    return (
                        FootballStatsCategory.CONTINUED === s.category &&
                        s.values?.from === FootballStatsCategory.FUMBLE &&
                        s.values?.offensive
                    );
                })
            ),
            lost: this.statCategorySummarizer(
                FootballStatsCategory.FUMBLE_LOST
            ),
            total: this.statCategorySummarizer(FootballStatsCategory.FUMBLE)
        };
    }

    defenseFumble() {
        return {
            forced: this.statCategorySummarizer(
                FootballStatsCategory.FUMBLE_FORCED
            ),
            recovered: this.statCategorySummarizer(
                FootballStatsCategory.FUMBLE_RECOVERED
            ),
            returned: this.statSummarizer(
                this.statLogs.filter((s) => {
                    return (
                        FootballStatsCategory.CONTINUED === s.category &&
                        s.values?.from === FootballStatsCategory.FUMBLE
                    );
                })
            ),
            continued: this.statSummarizer(
                this.statLogs.filter((s) => {
                    return (
                        FootballStatsCategory.CONTINUED === s.category &&
                        s.values?.from === FootballStatsCategory.FUMBLE &&
                        !s.values?.offensive
                    );
                })
            )
        };
    }

    defensiveSacks() {
        return this.statCategorySummarizer(FootballStatsCategory.DF_SACK);
    }

    offensiveSacks() {
        return this.statCategorySummarizer(FootballStatsCategory.OF_SACK);
    }

    defensiveTurnoverSummary() {
        return {};
    }

    defensiveInterceptionSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.INTERCEPTION);
    }

    defensiveInterceptionReturnsSummary() {
        return this.statCategorySummarizer(
            FootballStatsCategory.INTERCEPTION_RETURN
        );
    }

    puntSummary() {
        return {
            ...this.statCategorySummarizer(FootballStatsCategory.PUNT),
            blocked: this.statCategorySummarizer(
                FootballStatsCategory.PUNT_BLOCKED
            ),
            in20: this.statCategorySummarizer(FootballStatsCategory.PUNT_IN20)
                .completed
        };
    }

    puntReturnSummary() {
        return this.statCategorySummarizer(FootballStatsCategory.PUNT_RETURN);
    }

    returnsSummary() {
        return this.statCategorySummarizer([
            FootballStatsCategory.PUNT_RETURN,
            FootballStatsCategory.INTERCEPTION_RETURN
        ]);
    }

    statCategorySummarizer(category) {
        category = Array.isArray(category) ? category : [category];
        return this.statSummarizer(
            this.statLogs.filter((s) => {
                return category.includes(s.category);
            })
        );
    }

    statSummarizer(filtered) {
        const reduced = filtered.reduce(
            (carry, current) => {
                const yards = getSafeOrDefault(() => current.values.yards, 0);
                const attempted = getSafeOrDefault(
                    () => current.values.attempted,
                    0
                );
                const completed = getSafeOrDefault(
                    () => current.values.completed,
                    0
                );
                const touchdowns = getSafeOrDefault(
                    () => current.values.touchdowns,
                    0
                );
                const unassisted = getSafeOrDefault(
                    () => current.values.unassisted,
                    0
                );
                const points = getSafeOrDefault(() => current.values.points, 0);
                const interceptions = getSafeOrDefault(
                    () => current.values.interceptions,
                    0
                );
                const touchback = getSafeOrDefault(
                    () => current.values.touchback,
                    0
                );
                const rouge = getSafeOrDefault(() => current.values.rouge, 0);
                const dropKick = getSafeOrDefault(
                    () => current.values.dropKick,
                    0
                );

                const result = {
                    absoluteYards: carry.absoluteYards + Math.abs(yards),
                    totalYards: carry.totalYards + yards,
                    completed: carry.completed + completed,
                    attempted: carry.attempted + attempted,
                    longestYard:
                        yards > carry.longestYard ? yards : carry.longestYard,
                    gainedYards:
                        yards > 0
                            ? yards + carry.gainedYards
                            : carry.gainedYards,
                    gainedPlays:
                        yards > 0 ? 1 + carry.gainedPlays : carry.gainedPlays,
                    lostYards:
                        yards < 0
                            ? Math.abs(yards) + carry.lostYards
                            : carry.lostYards,
                    lostPlays:
                        yards < 0 ? 1 + carry.lostPlays : carry.lostPlays,
                    touchdowns: carry.touchdowns + touchdowns,
                    unassisted: carry.unassisted + unassisted,
                    points: carry.points + points,
                    interceptions: carry.interceptions + interceptions,
                    touchback: carry.touchback + touchback,
                    rouge: carry.rouge + rouge
                };
                const newDropKick = (carry.dropKick || 0) + dropKick;
                if (newDropKick > 0) {
                    result.dropKick = newDropKick;
                }
                // dropKick flags the attempt; score calculations need the
                // made count, so track completed drop kicks separately.
                const newDropKickCompleted =
                    (carry.dropKickCompleted || 0) +
                    (dropKick && completed ? 1 : 0);
                if (newDropKickCompleted > 0) {
                    result.dropKickCompleted = newDropKickCompleted;
                }
                return result;
            },
            {
                yards: 0,
                completed: 0,
                attempted: 0,
                totalYards: 0,
                absoluteYards: 0,
                longestYard: 0,
                gainedYards: 0,
                gainedPlays: 0,
                lostYards: 0,
                lostPlays: 0,
                touchdowns: 0,
                unassisted: 0,
                points: 0,
                interceptions: 0,
                touchback: 0,
                rouge: 0
            }
        );

        reduced.averageYards =
            reduced.completed > 0 ? reduced.totalYards / reduced.completed : 0;
        reduced.percent =
            reduced.attempted > 0 ? reduced.completed / reduced.attempted : 0;

        return reduced;
    }
}
